import { useCallback, useEffect, useMemo, useState } from 'react';
import { Lease } from '../../types/leases';
import { stairwellsApi } from '../../api/stairwellsApi';
import { unitsApi } from '../../api/unitsApi';
import { UNIT_LEASE_OBJECT_TYPES, leaseObjectLabelWithStairwell } from '../../Helpers/leaseUtils';
import { unitStairwellName } from '../../Helpers/stairwellUtils';

/** Per building: unit key → stairwell name, for the units that have one. */
type StairwellNames = { [buildingKey: string]: { [unitKey: string]: string } };

const isUnitLease = (lease: Pick<Lease, 'object'>): boolean =>
    UNIT_LEASE_OBJECT_TYPES.includes(lease.object.type) && !!lease.object.key;

const loadBuilding = async (buildingKey: string): Promise<{ [unitKey: string]: string }> => {
    const stairwells = await stairwellsApi.getStairwells(buildingKey);
    // Most buildings have none: skip reading their units.
    if (Object.keys(stairwells).length === 0) return {};
    const units = await unitsApi.getUnits(buildingKey);
    const names: { [unitKey: string]: string } = {};
    for (const unit of Object.values(units)) {
        const name = unitStairwellName(unit, stairwells);
        if (name) names[unit.key] = name;
    }
    return names;
};

/**
 * The stairwell of the unit behind each apartment or lokal lease, looked up
 * live (nothing is stored on the lease, so renames carry through). Reads the
 * stairwells of the buildings the leases are in, and their units only where
 * a building has stairwells. A failed read just shows no stairwell.
 * `objectLabel` is `leaseObjectLabel` plus " · Uppg. A" when there is one.
 */
export const useLeaseStairwells = (leases: Lease[]): { objectLabel: (lease: Pick<Lease, 'object' | 'buildingKey'>) => string } => {
    const [names, setNames] = useState<StairwellNames>({});

    // A stable key so a new leases array with the same buildings does not read again.
    const buildingKeysKey = useMemo(
        () => Array.from(new Set(leases.filter(isUnitLease).map(lease => lease.buildingKey))).sort().join(','),
        [leases]
    );

    useEffect(() => {
        const buildingKeys = buildingKeysKey ? buildingKeysKey.split(',') : [];
        if (buildingKeys.length === 0) {
            setNames({});
            return;
        }
        let cancelled = false;
        Promise.all(buildingKeys.map(key => loadBuilding(key).catch((err: unknown) => {
            console.warn(`Kunde inte läsa trappuppgångar för ${key}`, err);
            return {};
        })))
            .then(results => {
                if (cancelled) return;
                const next: StairwellNames = {};
                buildingKeys.forEach((key, index) => { next[key] = results[index]; });
                setNames(next);
            });
        return () => { cancelled = true; };
    }, [buildingKeysKey]);

    const objectLabel = useCallback((lease: Pick<Lease, 'object' | 'buildingKey'>): string => {
        const stairwellName = isUnitLease(lease) && lease.object.key ? names[lease.buildingKey]?.[lease.object.key] : undefined;
        return leaseObjectLabelWithStairwell(lease, stairwellName);
    }, [names]);

    return { objectLabel };
};
